import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { TEMPLATE_MODES } from "./cloneTemplateUtils";
import "./ModePickerModal.css";

// =============================================================================
// MODE PICKER MODAL
//
// Shown when the user clicks "+ Add New" on a template-type group.
// Lets them choose between the existing Standard Mode (pre-written copy
// templates) and the new Voice Clone Mode (style + structure variants).
//
// Both options return a TEMPLATE_MODES value to the parent which then
// dispatches into the right creation flow.
//
// Portalled onto <body>: World Settings clips nested dialogs and keeps the
// dashboard header above any in-pane overlay.
// =============================================================================

const ModePickerModal = ({ open, onPick, onClose, typeLabel }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="cct-modal-overlay bcScrim" onClick={onClose}>
      <div
        className="cct-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cct-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="cct-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark" />
        </button>

        <div className="cct-modal-header">
          <div className="cct-modal-eyebrow">New {typeLabel || "Template"}</div>
          <h2 id="cct-modal-title" className="cct-modal-title">
            Pick a creation mode
          </h2>
          <p className="cct-modal-subtitle">
            Both modes share the same generation pipeline and reply tracking.
            Pick the workflow that fits this campaign.
          </p>
        </div>

        <div className="cct-modal-grid">
          {/* STANDARD MODE — the existing well-known editor */}
          <button
            type="button"
            className="cct-mode-card"
            onClick={() => onPick(TEMPLATE_MODES.STANDARD)}
          >
            <div className="cct-mode-card-icon cct-mode-card-icon-standard">
              <i className="fa-solid fa-pen-to-square" />
            </div>
            <div className="cct-mode-card-title">Standard Mode</div>
            <div className="cct-mode-card-desc">
              Write pre-defined copy instructions, subject lines and let the
              AI personalize around them.
            </div>
            <ul className="cct-mode-card-bullets">
              <li>
                <i className="fa-solid fa-check" /> Familiar editor
              </li>
              <li>
                <i className="fa-solid fa-check" /> Direct copy control
              </li>
              <li>
                <i className="fa-solid fa-check" /> One structure per template
              </li>
            </ul>
            <div className="cct-mode-card-cta altBtn altBtn--petrol">
              Continue with Standard
              <i className="fa-solid fa-arrow-right" />
            </div>
          </button>

          {/* COPYTWIN MODE — voice-clone paradigm, branded as CopyTwin AI */}
          <button
            type="button"
            className="cct-mode-card cct-mode-card-clone"
            onClick={() => onPick(TEMPLATE_MODES.VOICE_CLONE)}
          >
            <div className="cct-mode-card-badge">NEW</div>
            <div className="cct-mode-card-icon cct-mode-card-icon-clone">
              <i className="fa-solid fa-wand-magic-sparkles" />
            </div>
            <div className="cct-mode-card-title">CopyTwin Mode</div>
            <div className="cct-mode-card-desc">
              No pre-written copy. Pick a CopyTwin — your AI copywriter twin —
              define info sources and A/B test multiple structures with
              per-variant reply tracking.
            </div>
            <ul className="cct-mode-card-bullets">
              <li>
                <i className="fa-solid fa-check" /> Your CopyTwin writes the style
              </li>
              <li>
                <i className="fa-solid fa-check" /> Choose info sources, not text
              </li>
              <li>
                <i className="fa-solid fa-check" /> Test 5+ structures in parallel
              </li>
              <li>
                <i className="fa-solid fa-check" /> Reply rate tracked per structure
              </li>
            </ul>
            <div className="cct-mode-card-cta altBtn altBtn--save">
              Build a CopyTwin Campaign
              <i className="fa-solid fa-arrow-right" />
            </div>
          </button>
        </div>

        <div className="cct-modal-footer">
          <i className="fa-solid fa-circle-info" />
          You can convert templates between modes later — but stats reset.
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModePickerModal;
